import { Button, Group, Modal, Text, Tooltip } from '@mantine/core';
import { IconForms, IconInfoCircle, IconPrinter, IconUpload } from '@tabler/icons-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShell } from '../../shell-context.js';
import docs from '../documents/Documents.module.css';
import type { DocumentPermissions, DocumentSource } from '../documents/source.js';
import type { LibraryDetail, LibrarySection } from '../documents/types.js';
import { OfficePackOfferModal } from '../settings/OfficePack.js';
import { type FillStep, FillSteps } from './FillSteps.js';
import forms from './Forms.module.css';
import { fillInAppFlow, newRecordFlow, openFormFlow, printFormFlow } from './library-flows.js';
import { NewRecordDialog } from './NewRecordDialog.js';
import { PdfFormFiller } from './PdfFormFiller.js';
import { inspectPdfForm, openPdfForm, type PdfjsModule } from './pdf-form.js';
import {
  type ActionGate,
  type FillCheck,
  formPageActions,
  formPermissions,
  libraryFormGates,
  paperRevisions,
  recordActionsNotice,
  recordPath,
  revisionSourceOf,
} from './record-model.js';
import type { FormRecordSource } from './source.js';
import { formErrorText } from './status.js';
import type { FormRecordDetail, FormTemplate } from './types.js';

export interface LibraryFormActionsInput {
  forms: FormRecordSource | undefined;
  documents: DocumentSource;
  section: LibrarySection;
  detail: LibraryDetail | null;
  permissions: DocumentPermissions;
  /**
   * Ekranın baskı yolu (M8a3 altlığı): altlığa "Record FR-…" eklenir; revizyonun baskı PDF'i
   * varsa o basılır (ADR 0042 §20; ADR 0028 #34(3)).
   */
  print: (options: { recordNo: string; renditionUrl: string | null }) => Promise<void>;
  onError: (error: unknown) => void;
  /** pdf.js yükleyicisi (test). */
  loadPdfjs?: () => Promise<PdfjsModule>;
  /** Önceden okunmuş şablon ve PDF denetimi (birim testi; ekran kendisi okur). */
  preload?: {
    template: FormTemplate | null;
    fillable: boolean;
    templateError?: string | null;
    fillCheck?: FillCheck;
  };
}

export interface LibraryFormActionsResult {
  /**
   * Başlık kartındaki tek birincil eylem (sahip kararı 2026-10-17; ADR 0074): Word/Excel'de
   * "Fill in" (Open Form; ortağın geri bildirimi 2026-10-16), PDF'te "Print" (Print Form) ve yanında
   * "Upload signed form" (New record).
   */
  primary: ReactNode;
  /** PDF yolunda başlıktaki Print kayıt açar: boş kopya basan ikinci Print çizilmez. */
  replacesPrint: boolean;
  /**
   * Sağ sütunun "How to fill in" kartının içi: açıklama, üç adım ve ikincil yol (Word'de kâğıt,
   * PDF'te basılmış kâğıdın kaydı ve alanlı PDF'te ekranda doldurma). Form Information ve Actions
   * kartlarının yerini alır.
   */
  guide: ReactNode;
  dialogs: ReactNode;
}

const defaultLoader = async () =>
  (await import('../documents/viewer-engines.js')).pdfjsLib() as unknown as Promise<PdfjsModule>;

type Pending = null | 'open' | 'print' | 'fill' | 'new';

const codeOf = (error: unknown) =>
  typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code: unknown }).code)
    : 'GENERIC';

/**
 * Form List'in kayıt eylemleri (ADR 0042 §18–§21; plan M8b Task 18): Open Form, Print Form,
 * Fill in app ve New record. Gemide ve `ism_forms.create` yetkisiyle; ofiste yoktur. Her yol
 * kayıt (taslak) açar ve kayıt sayfasına gider; taslak açılınca kayıt numarası ayrılır.
 */
export function useLibraryFormActions(
  input: LibraryFormActionsInput,
): LibraryFormActionsResult | null {
  const { t } = useTranslation();
  const shell = useShell();
  const hintId = useId();
  const ship = input.forms?.ship;
  const work = ship?.work;
  const detail = input.detail;
  const current = detail?.current ?? null;
  const active = Boolean(ship && input.section === 'ism' && detail?.kind === 'form');
  const revision = detail?.revisions.find((r) => r.id === current?.revisionId) ?? null;
  const [template, setTemplate] = useState<FormTemplate | null>(input.preload?.template ?? null);
  const [templateError, setTemplateError] = useState<string | null>(
    input.preload?.templateError ?? null,
  );
  const [fillable, setFillable] = useState(input.preload?.fillable ?? false);
  const [fillCheck, setFillCheck] = useState<FillCheck>(input.preload?.fillCheck ?? null);
  const [pending, setPending] = useState<Pending>(null);
  const [confirmPrint, setConfirmPrint] = useState(false);
  const [filling, setFilling] = useState(false);
  // Bir doldurma oturumunda açılan taslak (yeniden denemede aynı kayıt; sorular #522)
  const fillSession = useRef<{ draft: FormRecordDetail | null }>({ draft: null });
  const [creating, setCreating] = useState(false);
  const [missingApp, setMissingApp] = useState<string | null>(null);
  // İlk "Fill in"de Felixmate Office Pack önerisi (ADR 0070 §9); boyut bayt (0: bilinmiyor)
  const [packOffer, setPackOffer] = useState<{ size: number } | null>(null);
  const loadPdfjs = input.loadPdfjs ?? defaultLoader;
  const preloaded = input.preload !== undefined;

  const owner = detail?.owner;
  const revisionId = current?.revisionId ?? null;
  useEffect(() => {
    if (preloaded) return;
    setTemplate(null);
    setTemplateError(null);
    setFillable(false);
    // Şablon gelene ve PDF denetlenene kadar başlıktaki Fill in yükleniyor (yol sıçramaz)
    setFillCheck('pending');
    setMissingApp(null);
    if (!active || !ship) return;
    if (!revisionId) {
      setTemplateError('REVISION_NOT_CURRENT');
      return;
    }
    let cancelled = false;
    void ship
      .template(revisionSourceOf({ owner }), revisionId)
      .then((info) => {
        if (!cancelled) setTemplate(info);
      })
      .catch((error: unknown) => {
        if (!cancelled) setTemplateError(codeOf(error));
      });
    return () => {
      cancelled = true;
    };
  }, [preloaded, active, ship, owner, revisionId]);

  // Fill in app: sunucu adaylığından sonra alan, XFA ve betik denetimi pdf.js ile (§19; Ek U32)
  const templateUrl = revision ? input.documents.fileUrl(revision.file.id, 'preview') : null;
  const candidate = template?.fillInApp.allowed === true;
  useEffect(() => {
    if (preloaded || !candidate || !templateUrl) return;
    let cancelled = false;
    setFillCheck('pending');
    // İndirilemeyen ya da açılamayan PDF'te düğme sessizce kaybolmaz: nedenli pasif (2026-10-16)
    const failed = () => {
      if (cancelled) return;
      setFillable(false);
      setFillCheck('failed');
    };
    void (async () => {
      try {
        const response = await fetch(templateUrl);
        if (!response.ok) return failed();
        const pdfjs = await loadPdfjs();
        const doc = await openPdfForm(pdfjs, new Uint8Array(await response.arrayBuffer()));
        const inspection = await inspectPdfForm(doc);
        await doc.loadingTask.destroy();
        if (cancelled) return;
        setFillable(inspection.fillable);
        setFillCheck(null);
      } catch {
        failed();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [preloaded, candidate, templateUrl, loadPdfjs]);

  if (!active || !ship || !detail) return null;
  const gates = libraryFormGates({
    forms: formPermissions(input.permissions),
    template,
    templateError,
    fillable,
    fillCheck,
  });
  if (!gates.newRecord.visible) return null;
  // Birincil eylem dosya türünden (ADR 0074); şablon okunurken yükleniyor
  const page = formPageActions({
    gates,
    kind: revision?.fileKind ?? template?.source.kind ?? null,
    checking: !template && !templateError,
  });
  if (!page) return null;
  const formName = `${t('forms.record.formNo', { no: detail.docNo })} - ${detail.title}`;
  const go = (id: string) => shell?.navigate(recordPath(id));
  // Hiçbir yolla kayıt açılamıyorsa neden ve sonraki adım görünür yazılır (ADR 0055 §1)
  const notice = recordActionsNotice(detail, {
    create: gates.newRecord.visible,
    newRecord: gates.newRecord.enabled,
  });

  /**
   * Kayıt eylemi düğmesi: kapı kapalıysa nedeni ipucunda ve ekran okuyucuda. `head`: başlık kartı
   * biçimi (Edit'in sarmalayıcısı; dar ekranda da görünür); değilse kartın küçük düğmesi.
   */
  const button = (
    key: Exclude<Pending, null>,
    gate: ActionGate,
    label: string,
    icon: ReactNode,
    onClick: () => void,
    options: {
      variant: 'filled' | 'default' | 'subtle';
      head?: boolean;
      loading?: boolean;
      /** Başlıktaki Fill in: `data-action="fill-in"`, başlattığı yol `data-fill-in`. */
      fillIn?: boolean;
    },
  ) => {
    if (!gate.visible) return null;
    const reason = gate.enabled || !gate.reason ? null : formErrorText(t, { code: gate.reason });
    const id = `${hintId}-${options.head ? 'head' : 'card'}-${key}`;
    const element = (
      <Button
        variant={options.variant}
        {...(options.head ? {} : { size: 'xs' as const })}
        className={options.head ? docs.headButton : undefined}
        leftSection={icon}
        disabled={!gate.enabled || (pending !== null && pending !== key)}
        loading={pending === key || options.loading === true}
        aria-describedby={reason ? id : undefined}
        onClick={onClick}
        {...(options.fillIn
          ? { 'data-action': 'fill-in', 'data-fill-in': key }
          : { 'data-action': key })}
      >
        {label}
      </Button>
    );
    const wrap = options.head ? docs.headEdit : undefined;
    return reason ? (
      <span className={wrap}>
        <Tooltip label={reason} withArrow multiline w={260}>
          <span className={wrap}>{element}</span>
        </Tooltip>
        <span id={id} className={docs.srOnly}>
          {reason}
        </span>
      </span>
    ) : options.head ? (
      <span className={wrap}>{element}</span>
    ) : (
      element
    );
  };

  const startFill = () => {
    fillSession.current = { draft: null };
    setFilling(true);
  };

  const openForm = async (offered = false) => {
    if (!template || !revision) return;
    // Paket kurulu değilse ilk açılışta önerilir; cevaptan sonra form yine açılır (eski yol)
    if (!offered && work?.officePack) {
      const offer = await work.officePack.offer().catch(() => null);
      if (offer) {
        setPackOffer(offer);
        return;
      }
    }
    setPending('open');
    setMissingApp(null);
    try {
      const outcome = await openFormFlow({
        ship,
        detail,
        revision,
        template,
        discardReason: t('forms.actions.noAppDiscard'),
      });
      if (outcome.kind === 'no_app') setMissingApp(outcome.recordNo);
      else {
        if (outcome.kind === 'failed') input.onError({ code: outcome.code });
        go(outcome.record.id);
      }
    } catch (error) {
      input.onError(error);
    } finally {
      setPending(null);
    }
  };

  const printForm = async () => {
    if (!revision) return;
    setConfirmPrint(false);
    setPending('print');
    try {
      const { record, printError } = await printFormFlow({
        ship,
        detail,
        revision,
        fileUrl: (fileId) => input.documents.fileUrl(fileId, 'preview'),
        print: input.print,
      });
      if (printError) input.onError(printError);
      go(record.id);
    } catch (error) {
      input.onError(error);
    } finally {
      setPending(null);
    }
  };

  const askPrint = () => setConfirmPrint(true);
  const askUpload = () => setCreating(true);
  const officeRoute = page.route === 'office';

  // Başlık: tek birincil eylem (ADR 0074)
  const primary = officeRoute ? (
    button(
      'open',
      page.primary.gate,
      t('forms.actions.fillIn'),
      <IconForms size={16} />,
      () => void openForm(),
      { variant: 'filled', head: true, loading: page.checking, fillIn: true },
    )
  ) : (
    <>
      {button(
        'print',
        page.primary.gate,
        t('forms.page.print'),
        <IconPrinter size={16} />,
        askPrint,
        {
          variant: 'filled',
          head: true,
          loading: page.checking,
        },
      )}
      {button(
        'new',
        page.upload,
        t('forms.page.uploadSigned'),
        <IconUpload size={16} />,
        askUpload,
        { variant: 'default', head: true },
      )}
    </>
  );

  const steps: FillStep[] = (['step1', 'step2', 'step3'] as const).map((step) => ({
    title: t(`forms.page.${page.route}.${step}`),
    text: t(`forms.page.${page.route}.${step}Text`),
    // İlk adım başlıktaki birincil düğmeyle aynı renkte: göz oradan başlar
    state: step === 'step1' ? ('current' as const) : undefined,
  }));

  const guide = (
    <div className={forms.fillCard}>
      {detail.description ? <p className={forms.fillDescription}>{detail.description}</p> : null}
      <FillSteps steps={steps} />
      {notice ? (
        <div className={docs.officeNote} role="note" data-record-notice>
          <IconInfoCircle size={16} aria-hidden="true" />
          <p>{t(notice.key, notice.values)}</p>
        </div>
      ) : null}
      {missingApp ? (
        <p className={docs.muted} role="alert">
          {t('forms.actions.noApp', { record: missingApp })}
        </p>
      ) : null}
      {officeRoute ? (
        // Word/Excel formunu kâğıtta doldurmak ikinci yoldur: ayrı bölümde, açıkça kâğıt için
        <div className={forms.fillSecondary}>
          <p className={forms.fillSecondaryTitle}>{t('forms.page.onPaper')}</p>
          <p className={forms.fillText}>{t('forms.page.onPaperText')}</p>
          <div className={forms.fillSecondaryButtons}>
            {page.paperPrint
              ? button(
                  'print',
                  page.paperPrint,
                  t('forms.page.printForRecord'),
                  <IconPrinter size={14} />,
                  askPrint,
                  { variant: 'default' },
                )
              : null}
            {button(
              'new',
              page.upload,
              t('forms.page.upload'),
              <IconUpload size={14} />,
              askUpload,
              {
                variant: 'default',
              },
            )}
          </div>
        </div>
      ) : (
        <div className={forms.fillSecondary}>
          {/* Basılmış kâğıdın kaydı zaten açıktır: ikinci kayıt açılmasın (ADR 0072 ağacı) */}
          <p className={forms.fillText}>{t('forms.page.paperRecordHint')}</p>
          {page.fillInApp.visible ? (
            <>
              <p className={forms.fillText}>{t('forms.page.fillable')}</p>
              <div className={forms.fillSecondaryButtons}>
                {button(
                  'fill',
                  page.fillInApp,
                  t('forms.page.fillInApp'),
                  <IconForms size={14} />,
                  startFill,
                  { variant: 'default' },
                )}
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );

  return {
    primary,
    replacesPrint: !officeRoute,
    guide,
    dialogs: (
      <>
        <OfficePackOfferModal
          opened={packOffer !== null}
          size={packOffer?.size || null}
          variant="fillIn"
          onAnswer={(download) => {
            setPackOffer(null);
            void work?.officePack?.answer(download).catch(() => undefined);
            void openForm(true);
          }}
        />
        <Modal
          opened={confirmPrint}
          onClose={() => setConfirmPrint(false)}
          title={t('forms.actions.printTitle')}
          closeButtonProps={{ 'aria-label': t('forms.dialog.back') }}
        >
          <Text size="sm">{t('forms.actions.printBody', { form: formName })}</Text>
          <Group justify="flex-end" mt="lg">
            <Button variant="default" onClick={() => setConfirmPrint(false)}>
              {t('forms.dialog.back')}
            </Button>
            <Button leftSection={<IconPrinter size={16} />} onClick={() => void printForm()}>
              {t('forms.actions.printConfirm')}
            </Button>
          </Group>
        </Modal>
        <NewRecordDialog
          opened={creating}
          formName={formName}
          current={
            revision && template?.current
              ? { id: revision.id, revisionNo: revision.revisionNo }
              : null
          }
          paper={paperRevisions(detail)}
          onClose={() => setCreating(false)}
          onCreate={async (values) => {
            const record = await newRecordFlow({ ship, detail, values });
            setCreating(false);
            go(record.id);
          }}
        />
        {work && revision ? (
          <PdfFormFiller
            opened={filling}
            url={templateUrl}
            title={t('forms.fill.title', { record: formName })}
            loadPdfjs={loadPdfjs}
            onClose={() => {
              setFilling(false);
              // Kaydetme yarıda kaldıysa açılan taslak kaybolmaz: kayıt sayfası (orada yeniden
              // Fill in app ya da Discard) [sorular #522]
              const draft = fillSession.current.draft;
              if (draft) go(draft.id);
            }}
            onSave={async (bytes) => {
              const record = await fillInAppFlow({
                ship,
                detail,
                revision,
                bytes,
                session: fillSession.current,
              });
              setFilling(false);
              go(record.id);
            }}
          />
        ) : null}
      </>
    ),
  };
}
